import React, { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import {
  FolderOpen,
  Check,
  X,
  RefreshCw,
  HardDrive,
  FileText,
  Clock,
  AlertCircle,
  Loader2,
  Sparkles,
  Zap,
  StopCircle,
  FolderSync,
  RotateCcw,
  Plus,
  Copy,
  AlertTriangle,
  TrendingUp,
  Trash2,
  History,
  Eye,
  ChevronDown,
  ChevronUp,
  Folder,
  Upload,
  Cloud,
  CheckCircle2,
  XCircle,
  UploadCloud,
  Download,
  ExternalLink
} from 'lucide-react'
import { adminApi, specificationsApi, searchApi } from '../../services/api'
import FTPConfig from './FTPConfig'

// Local storage key for folder history
const FOLDER_HISTORY_KEY = 'diesim_folder_history'

// Helper to get folder history from localStorage
const getFolderHistory = () => {
  try {
    const history = localStorage.getItem(FOLDER_HISTORY_KEY)
    return history ? JSON.parse(history) : []
  } catch {
    return []
  }
}

// Helper to save folder to history
const saveFolderToHistory = (path) => {
  try {
    const history = getFolderHistory()
    // Remove if already exists
    const filtered = history.filter(h => h.path !== path)
    // Add to front with timestamp
    const newHistory = [
      { path, timestamp: new Date().toISOString() },
      ...filtered
    ].slice(0, 5) // Keep last 5
    localStorage.setItem(FOLDER_HISTORY_KEY, JSON.stringify(newHistory))
    return newHistory
  } catch {
    return []
  }
}

export default function RepositoryConfig() {
  const queryClient = useQueryClient()
  const [folderPath, setFolderPath] = useState('')
  const [currentJobId, setCurrentJobId] = useState(null)
  const [extractionProgress, setExtractionProgress] = useState(null)
  const [showFolderContents, setShowFolderContents] = useState(false)
  const [showExtractedFiles, setShowExtractedFiles] = useState(true) // Default to showing extracted files
  const [showHistory, setShowHistory] = useState(false)
  const [folderHistory, setFolderHistory] = useState(getFolderHistory())

  // Cloud storage upload state
  const [uploadStatus, setUploadStatus] = useState(null) // null, 'uploading', 'success', 'error'
  const [uploadMessage, setUploadMessage] = useState('')
  const [recentUploads, setRecentUploads] = useState([])
  const [showLocalFolder, setShowLocalFolder] = useState(false) // Toggle for legacy folder config

  // Fetch repository status
  const { data: repoStatus, isLoading, error, refetch } = useQuery({
    queryKey: ['repositoryStatus'],
    queryFn: () => adminApi.getRepositoryStatus().then(res => res.data),
    refetchInterval: currentJobId ? 3000 : false // Poll when job is running
  })

  // Fetch folder contents (PDF files in the folder)
  const { data: folderContents, isLoading: loadingContents, refetch: refetchContents } = useQuery({
    queryKey: ['folderContents', repoStatus?.repository_path],
    queryFn: () => adminApi.browseDirectory(repoStatus?.repository_path).then(res => res.data),
    enabled: !!repoStatus?.repository_path && repoStatus?.is_accessible && showFolderContents
  })

  // Fetch extracted specifications
  const { data: extractedFiles, isLoading: loadingExtracted, refetch: refetchExtracted } = useQuery({
    queryKey: ['extractedFiles'],
    queryFn: () => specificationsApi.list({ limit: 100, sort_by: 'created_at', sort_order: 'desc' }).then(res => res.data),
    enabled: showExtractedFiles
  })

  // Cloud storage connection test
  const { data: cloudStorageStatus, isLoading: loadingCloudStorage, refetch: refetchCloudStorage } = useQuery({
    queryKey: ['cloudStorageConnection'],
    queryFn: () => adminApi.storage.testConnection().then(res => res.data),
    refetchOnWindowFocus: false
  })

  // Fetch cloud storage files
  const { data: cloudStorageFiles, isLoading: loadingCloudStorageFiles, refetch: refetchCloudStorageFiles } = useQuery({
    queryKey: ['cloudStorageFiles'],
    queryFn: () => adminApi.storage.listFiles('').then(res => res.data),
    enabled: cloudStorageStatus?.success,
    refetchInterval: false
  })

  // Fetch current job status if we have one
  const { data: jobStatus } = useQuery({
    queryKey: ['jobStatus', currentJobId],
    queryFn: () => adminApi.getJob(currentJobId).then(res => res.data),
    enabled: !!currentJobId,
    refetchInterval: 2000 // Poll every 2 seconds
  })

  // Update extraction progress when job status changes
  useEffect(() => {
    if (jobStatus) {
      setExtractionProgress({
        status: jobStatus.status,
        total: jobStatus.progress?.total || 0,
        processed: jobStatus.progress?.processed || 0,
        successful: jobStatus.progress?.successful || 0,
        failed: jobStatus.progress?.failed || 0,
        percent: jobStatus.progress?.percent || 0,
        message: jobStatus.status_message,
        duplicate_files: jobStatus.duplicate_files || [],
        review_files: jobStatus.review_files || []
      })

      // Clear job if completed/failed/cancelled (but keep progress visible longer)
      if (['completed', 'failed', 'cancelled'].includes(jobStatus.status)) {
        setTimeout(() => {
          setCurrentJobId(null)
          queryClient.invalidateQueries(['repositoryStatus'])
        }, 10000) // Keep visible for 10 seconds to show results
      }
    }
  }, [jobStatus, queryClient])

  // State for mount instructions modal
  const [mountInstructions, setMountInstructions] = useState(null)

  // Initialize folder path from repo status
  useEffect(() => {
    if (repoStatus?.repository_path && !folderPath) {
      // Convert container path back to host path for display
      const containerPath = repoStatus.repository_path
      if (containerPath.startsWith('/mnt/')) {
        // Windows path: /mnt/d/CAD -> D:/CAD
        const driveLetter = containerPath.charAt(5).toUpperCase()
        const restOfPath = containerPath.substring(6)
        setFolderPath(`${driveLetter}:${restOfPath}`)
      } else if (containerPath.startsWith('/host/')) {
        // Mac/Linux path: /host/Users/name/folder -> /Users/name/folder
        setFolderPath(containerPath.substring(5))
      } else if (containerPath === '/app/cad') {
        setFolderPath('D:\\CAD')
      } else {
        setFolderPath(containerPath)
      }
    }
  }, [repoStatus?.repository_path])

  // Configure repository mutation
  const configureMutation = useMutation({
    mutationFn: (path) => {
      console.log('Configuring repository path:', path)
      return adminApi.configureRepository(path)
    },
    onSuccess: (response) => {
      console.log('Repository configured successfully:', response.data)
      queryClient.invalidateQueries(['repositoryStatus'])

      // Save to history
      const newHistory = saveFolderToHistory(folderPath)
      setFolderHistory(newHistory)

      // Show folder contents automatically
      setShowFolderContents(true)

      // Check if the path needs to be mounted
      if (response.data?.needs_mount) {
        setMountInstructions({
          windowsPath: response.data.repository_path,
          containerPath: response.data.container_path,
          volumeLine: response.data.volume_line,
          message: response.data.message
        })
      }
    },
    onError: (error) => {
      console.error('Repository configuration failed:', error)
    }
  })

  // Delete specification mutation
  const deleteSpecMutation = useMutation({
    mutationFn: (id) => specificationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['extractedFiles'])
      queryClient.invalidateQueries(['repositoryStatus'])
      refetchExtracted()
    }
  })

  // Clear all extractions mutation
  const clearAllMutation = useMutation({
    mutationFn: () => adminApi.clearAllExtractions(),
    onSuccess: () => {
      queryClient.invalidateQueries(['extractedFiles'])
      queryClient.invalidateQueries(['repositoryStatus'])
      setShowExtractedFiles(false)
    }
  })

  // Start extraction mutation
  const extractMutation = useMutation({
    mutationFn: (config) => adminApi.startProcessing(config),
    onSuccess: (response) => {
      setCurrentJobId(response.data.job_id)
      setExtractionProgress({
        status: 'starting',
        total: response.data.progress?.total || 0,
        processed: 0,
        successful: 0,
        failed: 0,
        percent: 0,
        message: 'Starting extraction...'
      })
    }
  })

  // Cancel job mutation
  const cancelMutation = useMutation({
    mutationFn: (jobId) => adminApi.controlJob(jobId, 'cancel'),
    onSuccess: () => {
      setExtractionProgress(prev => ({ ...prev, status: 'cancelled', message: 'Cancelled by user' }))
    }
  })

  // Rescan repository mutation
  const rescanMutation = useMutation({
    mutationFn: (path) => adminApi.rescanRepository(path),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['repositoryStatus'])
      if (response.data.new_files_count > 0) {
        alert(`Found ${response.data.new_files_count} new PDF files ready for extraction!`)
      } else {
        alert('All PDF files have already been processed. No new files found.')
      }
    }
  })

  // Extract new files mutation
  const extractNewMutation = useMutation({
    mutationFn: (path) => adminApi.extractNewFiles(path, true),
    onSuccess: (response) => {
      if (response.data.job_id) {
        setCurrentJobId(response.data.job_id)
        setExtractionProgress({
          status: 'starting',
          total: response.data.new_files_count || 0,
          processed: 0,
          successful: 0,
          failed: 0,
          percent: 0,
          message: 'Starting extraction of new files...'
        })
      } else {
        alert(response.data.message || 'No new files to process.')
      }
    }
  })

  // Reprocess all mutation
  const reprocessAllMutation = useMutation({
    mutationFn: (path) => adminApi.reprocessAll(path, true),
    onSuccess: (response) => {
      if (response.data.job_id) {
        setCurrentJobId(response.data.job_id)
        setExtractionProgress({
          status: 'starting',
          total: response.data.total_files_count || 0,
          processed: 0,
          successful: 0,
          failed: 0,
          percent: 0,
          message: 'Starting reprocessing of all files...'
        })
      } else {
        alert(response.data.message || 'No files to process.')
      }
    }
  })

  // Cloud storage upload mutation
  const uploadToCloudStorageMutation = useMutation({
    mutationFn: (file) => adminApi.storage.uploadFile(file, 'master', true),
    onMutate: () => {
      setUploadStatus('uploading')
      setUploadMessage('Uploading and extracting...')
    },
    onSuccess: (response) => {
      const data = response.data
      if (data.success) {
        if (data.is_duplicate) {
          setUploadStatus('error')
          setUploadMessage(`Duplicate file: Already exists as "${data.duplicate_of}"`)
        } else {
          setUploadStatus('success')
          const msg = data.extraction_success
            ? `Uploaded and extracted: ${data.extracted_section_number || data.file_path}`
            : `Uploaded: ${data.file_path} (extraction pending)`
          setUploadMessage(msg)

          // Add to recent uploads
          setRecentUploads(prev => [{
            name: data.file_path || data.filename,
            success: data.extraction_success,
            section: data.extracted_section_number,
            confidence: data.extraction_confidence,
            timestamp: new Date().toISOString()
          }, ...prev].slice(0, 5))

          // Refresh file list
          queryClient.invalidateQueries(['cloudStorageFiles'])
          queryClient.invalidateQueries(['extractedFiles'])
          refetchCloudStorageFiles()
        }
      } else {
        setUploadStatus('error')
        setUploadMessage(data.error || 'Upload failed')
      }

      // Clear status after 5 seconds
      setTimeout(() => {
        setUploadStatus(null)
        setUploadMessage('')
      }, 5000)
    },
    onError: (error) => {
      setUploadStatus('error')
      setUploadMessage(error.response?.data?.detail || error.message || 'Upload failed')
      setTimeout(() => {
        setUploadStatus(null)
        setUploadMessage('')
      }, 5000)
    }
  })

  // Cloud storage delete mutation
  const deleteCloudStorageFileMutation = useMutation({
    mutationFn: (filePath) => adminApi.storage.deleteFile(filePath, true),
    onSuccess: () => {
      queryClient.invalidateQueries(['cloudStorageFiles'])
      queryClient.invalidateQueries(['extractedFiles'])
      refetchCloudStorageFiles()
    }
  })

  // Cloud storage extract mutation (for files uploaded without auto-extract)
  const extractCloudStorageFileMutation = useMutation({
    mutationFn: (filePath) => adminApi.storage.extractFile(filePath, true),
    onSuccess: () => {
      queryClient.invalidateQueries(['cloudStorageFiles'])
      queryClient.invalidateQueries(['extractedFiles'])
      refetchCloudStorageFiles()
    }
  })

  // Track upload queue for multiple files
  const [uploadQueue, setUploadQueue] = useState([])
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0)
  const [totalUploads, setTotalUploads] = useState(0)
  const [isProcessingQueue, setIsProcessingQueue] = useState(false)

  // Multi-select delete state
  const [selectedFiles, setSelectedFiles] = useState(new Set())
  const [isSelectMode, setIsSelectMode] = useState(false)

  // Process upload queue sequentially
  const processUploadQueue = useCallback(async (files) => {
    setIsProcessingQueue(true)
    const results = []

    for (let i = 0; i < files.length; i++) {
      setCurrentUploadIndex(i)
      setUploadStatus('uploading')
      setUploadMessage(`Uploading ${i + 1} of ${files.length}: ${files[i].name}`)

      try {
        const response = await adminApi.storage.uploadFile(files[i], 'master', true)
        const data = response.data

        if (data.success && !data.is_duplicate) {
          results.push({
            name: data.blob_name,
            success: data.extraction_success,
            section: data.extracted_section_number,
            confidence: data.extraction_confidence,
            timestamp: new Date().toISOString()
          })
        }
      } catch (error) {
        console.error(`Failed to upload ${files[i].name}:`, error)
      }
    }

    // All uploads complete
    setIsProcessingQueue(false)
    setUploadQueue([])
    setCurrentUploadIndex(0)
    setTotalUploads(0)

    if (results.length > 0) {
      setRecentUploads(prev => [...results, ...prev].slice(0, 10))
      setUploadStatus('success')
      setUploadMessage(`Successfully uploaded ${results.length} of ${files.length} files`)
    } else {
      setUploadStatus('error')
      setUploadMessage('No files were uploaded successfully')
    }

    // Refresh file list
    queryClient.invalidateQueries(['cloudStorageFiles'])
    queryClient.invalidateQueries(['extractedFiles'])
    refetchCloudStorageFiles()

    // Clear status after 5 seconds
    setTimeout(() => {
      setUploadStatus(null)
      setUploadMessage('')
    }, 5000)
  }, [queryClient, refetchCloudStorageFiles])

  // Dropzone for Cloud Storage upload - now supports multiple files
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      // Filter only PDF files
      const pdfFiles = acceptedFiles.filter(
        file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
      )

      if (pdfFiles.length === 0) {
        setUploadStatus('error')
        setUploadMessage('Only PDF files are allowed')
        setTimeout(() => {
          setUploadStatus(null)
          setUploadMessage('')
        }, 3000)
        return
      }

      // Set up queue for multiple uploads
      setUploadQueue(pdfFiles)
      setTotalUploads(pdfFiles.length)
      setCurrentUploadIndex(0)

      // Start processing the queue
      processUploadQueue(pdfFiles)
    }
  }, [processUploadQueue])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,  // Allow multiple file selection
    disabled: isProcessingQueue
  })

  // Track downloading state for each file
  const [downloadingFiles, setDownloadingFiles] = useState({})

  const handleDeleteCloudStorageFile = (filePath) => {
    if (window.confirm(`Delete "${filePath}" from Cloud Storage? This will also remove the extracted data.`)) {
      deleteCloudStorageFileMutation.mutate(filePath)
    }
  }

  const handleExtractCloudStorageFile = (filePath) => {
    extractCloudStorageFileMutation.mutate(filePath)
  }

  // Handle viewing/downloading PDF from Cloud Storage
  const handleViewPdf = async (filePath, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!filePath || downloadingFiles[filePath]) return

    // Check if it's a Cloud Storage path
    const isCloudStoragePath = filePath.startsWith('azure://') || filePath.startsWith('spaces://') || filePath.startsWith('master/') || filePath.includes('/')
    if (!isCloudStoragePath) return

    // Build the full storage path if needed
    let storagePath = filePath
    if (!filePath.startsWith('azure://') && !filePath.startsWith('spaces://')) {
      // Check provider type from cloudStorageStatus
      const providerType = cloudStorageStatus?.provider_type || 'spaces'
      const containerName = cloudStorageStatus?.container || 'cognavia-diesim'

      if (providerType === 'spaces') {
        storagePath = `spaces://${containerName}/${filePath}`
      } else {
        storagePath = `azure://${containerName}/${filePath}`
      }
    }

    setDownloadingFiles(prev => ({ ...prev, [filePath]: true }))
    try {
      const response = await searchApi.getDownloadUrl(storagePath)
      const downloadUrl = response.data.download_url
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Failed to get download URL:', error)
      alert('Failed to open PDF. Please try again.')
    } finally {
      setDownloadingFiles(prev => ({ ...prev, [filePath]: false }))
    }
  }

  // Multi-select handlers
  const toggleFileSelection = (filePath) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev)
      if (newSet.has(filePath)) {
        newSet.delete(filePath)
      } else {
        newSet.add(filePath)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectedFiles.size === cloudStorageFiles?.files?.length) {
      setSelectedFiles(new Set())
    } else {
      setSelectedFiles(new Set(cloudStorageFiles?.files?.map(f => f.path) || []))
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return

    const fileNames = Array.from(selectedFiles).map(path => {
      const file = cloudStorageFiles?.files?.find(f => f.path === path)
      return file?.section_number || file?.name || path
    }).join(', ')

    if (!window.confirm(`Delete ${selectedFiles.size} file(s) from Cloud Storage?\n\nFiles: ${fileNames}\n\nThis will also remove the extracted data. This cannot be undone.`)) {
      return
    }

    // Delete files one by one
    const filesToDelete = Array.from(selectedFiles)
    for (const filePath of filesToDelete) {
      try {
        await adminApi.storage.deleteFile(filePath, true)
      } catch (error) {
        console.error(`Failed to delete ${filePath}:`, error)
      }
    }

    // Clear selection and refresh
    setSelectedFiles(new Set())
    setIsSelectMode(false)
    queryClient.invalidateQueries(['cloudStorageFiles'])
    queryClient.invalidateQueries(['extractedFiles'])
    refetchCloudStorageFiles()
  }

  const cancelSelectMode = () => {
    setIsSelectMode(false)
    setSelectedFiles(new Set())
  }

  const handleConfigurePath = () => {
    if (folderPath.trim()) {
      configureMutation.mutate(folderPath.trim())
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && folderPath.trim()) {
      handleConfigurePath()
    }
  }

  const handleSelectFromHistory = (path) => {
    setFolderPath(path)
    setShowHistory(false)
    configureMutation.mutate(path)
  }

  const handleClearFolder = () => {
    if (window.confirm('This will clear the current folder configuration. Your extracted data will remain. Continue?')) {
      setFolderPath('')
      setShowFolderContents(false)
      // Just clear local state - keep data in DB
    }
  }

  const handleDeleteSpec = (id, fileName) => {
    if (window.confirm(`Delete extracted data for "${fileName}"? This cannot be undone.`)) {
      deleteSpecMutation.mutate(id)
    }
  }

  const handleClearAllExtractions = () => {
    if (window.confirm('This will delete ALL extracted specifications from the database. This cannot be undone!\n\nAre you sure?')) {
      clearAllMutation.mutate()
    }
  }

  const handleStartExtraction = () => {
    if (!repoStatus?.repository_path || !repoStatus?.is_accessible) return

    extractMutation.mutate({
      job_type: 'incremental',
      job_name: `AI Extraction - ${new Date().toLocaleString()}`,
      repository_path: repoStatus.repository_path,
      batch_size: 10,
      max_workers: 2,
      use_ocr_fallback: true,
      use_ai_fallback: true,
      skip_existing: true
    })
  }

  const handleCancelExtraction = () => {
    if (currentJobId) {
      cancelMutation.mutate(currentJobId)
    }
  }

  const handleRescan = () => {
    if (repoStatus?.repository_path) {
      rescanMutation.mutate(repoStatus.repository_path)
    }
  }

  const handleExtractNew = () => {
    if (repoStatus?.repository_path) {
      extractNewMutation.mutate(repoStatus.repository_path)
    }
  }

  const handleReprocessAll = () => {
    if (repoStatus?.repository_path && window.confirm(
      'This will re-extract ALL PDFs in the repository, replacing existing data.\n\nThis is useful when extraction logic has been improved.\n\nAre you sure you want to continue?'
    )) {
      reprocessAllMutation.mutate(repoStatus.repository_path)
    }
  }

  const isExtracting = currentJobId && extractionProgress &&
    !['completed', 'failed', 'cancelled'].includes(extractionProgress.status)

  const isAnyMutationPending = rescanMutation.isPending || extractNewMutation.isPending || reprocessAllMutation.isPending

  return (
    <div className="space-y-6">
      {/* Cloud Storage PDF Upload Section */}
      <div className="card group hover:shadow-xl transition-all duration-300">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center text-gray-800">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-cyan-50 mr-3">
              <UploadCloud className="w-5 h-5 text-blue-600" />
            </div>
            Upload PDF to Cloud Storage
          </h3>
          <div className="flex items-center gap-2">
            {/* Cloud Storage Connection Status */}
            {loadingCloudStorage ? (
              <span className="flex items-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                Checking...
              </span>
            ) : cloudStorageStatus?.success ? (
              <span className="flex items-center text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Cloud Storage Connected
              </span>
            ) : (
              <span className="flex items-center text-sm text-red-600 bg-red-50 px-3 py-1 rounded-full">
                <XCircle className="w-4 h-4 mr-1" />
                Cloud Storage Disconnected
              </span>
            )}
            <button
              onClick={() => refetchCloudStorage()}
              className="btn btn-secondary btn-sm group/btn"
              disabled={isExtracting}
            >
              <RefreshCw className="w-4 h-4 group-hover/btn:rotate-180 transition-transform duration-500" />
            </button>
          </div>
        </div>
        <div className="card-body">
          {/* Upload Dropzone */}
          <div className="space-y-4">
            <div>
              <label className="label flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
                Upload PDF File
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Drag and drop a PDF file or click to browse. Files are uploaded to Cloud Storage and automatically extracted.
              </p>

              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
                  isDragActive
                    ? 'border-blue-500 bg-blue-50'
                    : uploadStatus === 'uploading'
                    ? 'border-yellow-400 bg-yellow-50'
                    : uploadStatus === 'success'
                    ? 'border-green-500 bg-green-50'
                    : uploadStatus === 'error'
                    ? 'border-red-400 bg-red-50'
                    : cloudStorageStatus?.success
                    ? 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                }`}
              >
                <input {...getInputProps()} disabled={!cloudStorageStatus?.success || isProcessingQueue} />

                {uploadStatus === 'uploading' ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mb-3" />
                    <p className="text-yellow-700 font-medium">{uploadMessage}</p>
                    <p className="text-sm text-yellow-600 mt-1">Please wait...</p>
                  </div>
                ) : uploadStatus === 'success' ? (
                  <div className="flex flex-col items-center">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mb-3" />
                    <p className="text-green-700 font-medium">{uploadMessage}</p>
                  </div>
                ) : uploadStatus === 'error' ? (
                  <div className="flex flex-col items-center">
                    <XCircle className="w-12 h-12 text-red-500 mb-3" />
                    <p className="text-red-700 font-medium">{uploadMessage}</p>
                  </div>
                ) : isDragActive ? (
                  <div className="flex flex-col items-center">
                    <Upload className="w-12 h-12 text-blue-500 mb-3 animate-bounce" />
                    <p className="text-blue-700 font-medium">Drop the PDF here...</p>
                  </div>
                ) : cloudStorageStatus?.success ? (
                  <div className="flex flex-col items-center">
                    <Cloud className="w-12 h-12 text-gray-400 mb-3" />
                    <p className="text-gray-700 font-medium">Drag & drop a PDF file here</p>
                    <p className="text-sm text-gray-500 mt-1">or click to browse</p>
                    <p className="text-xs text-gray-400 mt-3">Max file size: 50MB • Auto-extraction enabled</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <AlertCircle className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">Cloud Storage not connected</p>
                    <p className="text-sm text-gray-400 mt-1">Please configure Cloud Storage credentials in environment</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Uploads */}
            {recentUploads.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-600 mb-2">Recent Uploads:</p>
                <div className="space-y-2">
                  {recentUploads.map((upload, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        {upload.success ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-500 mr-2" />
                        )}
                        <span className="text-sm text-gray-700">{upload.section || upload.name}</span>
                      </div>
                      {upload.confidence && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          upload.confidence >= 0.8 ? 'bg-green-100 text-green-700' :
                          upload.confidence >= 0.6 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {(upload.confidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cloud Storage Stats */}
            {cloudStorageStatus?.success && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium">Storage Account</p>
                  <p className="text-sm font-bold text-blue-800 truncate">{cloudStorageStatus.account}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium">Container</p>
                  <p className="text-sm font-bold text-blue-800 truncate">{cloudStorageStatus.container}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-xs text-green-600 font-medium">Files in Cloud Storage</p>
                  <p className="text-sm font-bold text-green-800">{cloudStorageFiles?.total_count || cloudStorageStatus.file_count || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs text-purple-600 font-medium">Total Size</p>
                  <p className="text-sm font-bold text-purple-800">{cloudStorageFiles?.total_size_mb?.toFixed(2) || 0} MB</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cloud Storage Files / Extracted PDFs Section */}
      <div className="card group hover:shadow-xl transition-all duration-300">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center text-gray-800">
            <div className="p-2 rounded-lg bg-gradient-to-br from-green-100 to-emerald-50 mr-3">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            Extracted PDFs
            {cloudStorageFiles && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({cloudStorageFiles.processed_count} processed / {cloudStorageFiles.total_count} total)
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {/* Multi-select controls */}
            {isSelectMode ? (
              <>
                <button
                  onClick={toggleSelectAll}
                  className="btn btn-secondary btn-sm"
                >
                  {selectedFiles.size === cloudStorageFiles?.files?.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={selectedFiles.size === 0}
                  className="btn btn-sm text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete ({selectedFiles.size})
                </button>
                <button
                  onClick={cancelSelectMode}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                {cloudStorageFiles?.files?.length > 0 && (
                  <button
                    onClick={() => setIsSelectMode(true)}
                    className="btn btn-secondary btn-sm"
                    title="Select multiple files to delete"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Select
                  </button>
                )}
                <button
                  onClick={() => refetchCloudStorageFiles()}
                  className="btn btn-secondary btn-sm group/btn"
                  disabled={loadingCloudStorageFiles}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingCloudStorageFiles ? 'animate-spin' : 'group-hover/btn:rotate-180 transition-transform duration-500'}`} />
                  Refresh
                </button>
              </>
            )}
          </div>
        </div>
        <div className="card-body">
          {loadingCloudStorageFiles ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              <span className="ml-2 text-gray-500">Loading files from Cloud Storage...</span>
            </div>
          ) : !cloudStorageStatus?.success ? (
            <div className="text-center py-8">
              <Cloud className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Cloud Storage not connected</p>
              <p className="text-sm text-gray-400 mt-1">Configure Cloud Storage credentials to see files</p>
            </div>
          ) : cloudStorageFiles?.files?.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {cloudStorageFiles.files.map((file, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors group/item ${
                    isSelectMode && selectedFiles.has(file.path)
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={isSelectMode ? () => toggleFileSelection(file.path) : undefined}
                  style={isSelectMode ? { cursor: 'pointer' } : undefined}
                >
                  <div className="flex items-center flex-1 min-w-0">
                    {/* Checkbox for select mode */}
                    {isSelectMode && (
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.path)}
                        onChange={() => toggleFileSelection(file.path)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    )}
                    <FileText className={`w-4 h-4 mr-3 flex-shrink-0 ${file.processed ? 'text-green-500' : 'text-gray-400'}`} />
                    <div className="min-w-0">
                      {/* Clickable file name to view PDF */}
                      <button
                        onClick={(e) => handleViewPdf(file.path, e)}
                        disabled={downloadingFiles[file.path]}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate flex items-center gap-1 max-w-full text-left"
                        title={`Click to view: ${file.name}`}
                      >
                        {downloadingFiles[file.path] ? (
                          <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                        ) : null}
                        <span className="truncate">{file.section_number || file.name}</span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-50" />
                      </button>
                      <p className="text-xs text-gray-500 truncate">
                        {file.path} • {file.size_kb} KB
                        {file.last_modified && ` • ${new Date(file.last_modified).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  {!isSelectMode && (
                    <div className="flex items-center gap-2 ml-3">
                      {file.processed ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          (file.extraction_confidence || 0) >= 0.8 ? 'bg-green-100 text-green-700' :
                          (file.extraction_confidence || 0) >= 0.6 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {((file.extraction_confidence || 0) * 100).toFixed(0)}%
                        </span>
                      ) : (
                        <button
                          onClick={() => handleExtractCloudStorageFile(file.path)}
                          disabled={extractCloudStorageFileMutation.isPending}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        >
                          {extractCloudStorageFileMutation.isPending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Extract'
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteCloudStorageFile(file.path)}
                        disabled={deleteCloudStorageFileMutation.isPending}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover/item:opacity-100 transition-all"
                        title="Delete from Cloud Storage"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No PDF files in Cloud Storage yet</p>
              <p className="text-sm text-gray-400 mt-1">Upload PDFs using the upload zone above</p>
            </div>
          )}
        </div>
      </div>

      {/* Legacy Local Folder Configuration (Collapsible) */}
      <div className="card">
        <button
          onClick={() => setShowLocalFolder(!showLocalFolder)}
          className="w-full card-header flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <h3 className="text-lg font-semibold flex items-center text-gray-800">
            <div className="p-2 rounded-lg bg-gradient-to-br from-gray-100 to-gray-50 mr-3">
              <FolderOpen className="w-5 h-5 text-gray-600" />
            </div>
            Local Folder Configuration
            <span className="ml-2 text-xs font-normal text-gray-400">(Advanced)</span>
          </h3>
          {showLocalFolder ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>

        {showLocalFolder && (
          <div className="card-body border-t border-gray-100">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="relative">
                  <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12 text-danger-600 bg-danger-50/50 rounded-xl">
                <AlertCircle className="w-6 h-6 mr-2" />
                <span className="font-medium">Failed to load repository status</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Folder Path Input */}
                <div>
                  <label className="label flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-500" />
                    Repository Folder Path
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    Enter the folder path containing your PDF files
                    <span className="block text-xs text-gray-400 mt-1">
                      Windows: D:\CAD\Drawings &nbsp;|&nbsp; Mac: /Users/yourname/Documents/CAD
                    </span>
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <FolderOpen className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={folderPath}
                        onChange={(e) => setFolderPath(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="D:\CAD\Drawings or /Users/name/Documents"
                        className="input pl-12 font-mono text-sm w-full"
                        disabled={isExtracting}
                      />
                    </div>
                    <button
                      onClick={handleConfigurePath}
                      disabled={!folderPath.trim() || configureMutation.isPending || isExtracting}
                      className="btn btn-primary"
                    >
                      {configureMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Set Path
                    </button>
                  </div>

                  {/* Status indicator */}
                  {repoStatus?.repository_path && (
                    <div className={`mt-3 flex items-center gap-2 p-3 rounded-lg ${
                      repoStatus.is_accessible
                        ? 'bg-success-50 border border-success-200'
                        : 'bg-danger-50 border border-danger-200'
                    }`}>
                      {repoStatus.is_accessible ? (
                        <>
                          <Check className="w-5 h-5 text-success-600" />
                          <span className="text-sm text-success-700 font-medium">
                            Folder connected successfully - {repoStatus.total_files || 0} PDF files found
                          </span>
                        </>
                      ) : (
                        <>
                          <X className="w-5 h-5 text-danger-600" />
                          <span className="text-sm text-danger-700 font-medium">
                            Folder not accessible - please check the path
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  {/* Error message */}
                  {configureMutation.error && (
                    <div className="mt-3 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                      <p className="text-sm text-danger-700">
                        {configureMutation.error.response?.data?.detail || 'Failed to configure repository. Make sure the path exists and is accessible.'}
                      </p>
                    </div>
                  )}

                  {/* History Dropdown */}
                  {folderHistory.length > 0 && (
                    <div className="mt-3">
                      <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
                      >
                        <History className="w-4 h-4 mr-1" />
                        Recent folders
                        {showHistory ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                      </button>
                      {showHistory && (
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200 space-y-1">
                          {folderHistory.map((item, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleSelectFromHistory(item.path)}
                              className="w-full flex items-center justify-between p-2 text-left text-sm hover:bg-white rounded-lg transition-colors group"
                            >
                              <div className="flex items-center">
                                <Folder className="w-4 h-4 text-amber-500 mr-2" />
                                <span className="font-mono text-gray-700 truncate max-w-md">{item.path}</span>
                              </div>
                              <span className="text-xs text-gray-400 group-hover:text-gray-600">
                                {new Date(item.timestamp).toLocaleDateString()}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

              {/* Folder Contents Section */}
              {repoStatus?.is_accessible && repoStatus?.total_files > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => {
                      setShowFolderContents(!showFolderContents)
                      if (!showFolderContents) refetchContents()
                    }}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center">
                      <Eye className="w-5 h-5 text-primary-600 mr-3" />
                      <span className="font-medium text-gray-800">View Folder Contents</span>
                      <span className="ml-2 text-sm text-gray-500">({repoStatus.total_files} PDF files)</span>
                    </div>
                    {showFolderContents ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>

                  {showFolderContents && (
                    <div className="p-4 bg-white border-t border-gray-200 max-h-64 overflow-y-auto">
                      {loadingContents ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                          <span className="ml-2 text-gray-500">Loading files...</span>
                        </div>
                      ) : folderContents?.files?.length > 0 ? (
                        <div className="space-y-2">
                          {folderContents.files.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                              <div className="flex items-center">
                                <FileText className="w-4 h-4 text-red-500 mr-2" />
                                <span className="text-sm text-gray-700">{file.name}</span>
                              </div>
                              <span className="text-xs text-gray-400">{file.size_kb} KB</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 py-4">No PDF files found in this folder</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Repository Stats - kept for local folder */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-5 bg-gradient-to-br from-primary-50/50 to-white rounded-xl border border-primary-100 hover:shadow-md transition-all duration-300 group/stat">
                  <div className="flex items-center text-gray-600 mb-3">
                    <div className="p-2 rounded-lg bg-primary-100 group-hover/stat:scale-110 transition-transform">
                      <FileText className="w-4 h-4 text-primary-600" />
                    </div>
                    <span className="ml-3 font-medium">Total Files</span>
                  </div>
                  <p className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                    {repoStatus?.total_files?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">PDF documents</p>
                </div>

                <div className="p-5 bg-gradient-to-br from-accent-cyan/10 to-white rounded-xl border border-cyan-100 hover:shadow-md transition-all duration-300 group/stat">
                  <div className="flex items-center text-gray-600 mb-3">
                    <div className="p-2 rounded-lg bg-cyan-100 group-hover/stat:scale-110 transition-transform">
                      <HardDrive className="w-4 h-4 text-accent-cyan" />
                    </div>
                    <span className="ml-3 font-medium">Total Size</span>
                  </div>
                  <p className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                    {repoStatus?.total_size_mb?.toFixed(2) || 0} MB
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Avg: {repoStatus?.avg_file_size_kb?.toFixed(1) || 0} KB/file
                  </p>
                </div>

                <div className="p-5 bg-gradient-to-br from-accent-purple/10 to-white rounded-xl border border-purple-100 hover:shadow-md transition-all duration-300 group/stat">
                  <div className="flex items-center text-gray-600 mb-3">
                    <div className="p-2 rounded-lg bg-purple-100 group-hover/stat:scale-110 transition-transform">
                      <Clock className="w-4 h-4 text-accent-purple" />
                    </div>
                    <span className="ml-3 font-medium">Last Scan</span>
                  </div>
                  <p className="text-xl font-bold text-gray-800">
                    {repoStatus?.last_scan_time
                      ? new Date(repoStatus.last_scan_time).toLocaleDateString()
                      : 'Never'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {repoStatus?.last_scan_time
                      ? new Date(repoStatus.last_scan_time).toLocaleTimeString()
                      : 'No scans yet'}
                  </p>
                </div>
              </div>

              {/* AI Extraction Section */}
              {repoStatus?.is_accessible && repoStatus?.total_files > 0 && (
                <div className="relative p-6 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 rounded-2xl border border-purple-200 overflow-hidden">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-purple-200/30 to-transparent rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-200/30 to-transparent rounded-full blur-3xl pointer-events-none" />

                  <div className="relative flex items-center justify-between mb-5">
                    <div className="flex items-center">
                      <div className="relative p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl mr-4 shadow-lg shadow-purple-500/30">
                        <Sparkles className="w-6 h-6 text-white" />
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 blur-lg opacity-50" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">AI-Powered Extraction</h4>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <Zap className="w-3 h-3 text-yellow-500" />
                          Extract data from PDFs using OpenAI GPT-4o-mini
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="relative flex flex-wrap gap-3 mb-4">
                    {!isExtracting ? (
                      <>
                        {/* Rescan for new files button */}
                        <button
                          onClick={handleRescan}
                          disabled={isAnyMutationPending}
                          className="btn btn-secondary group/scan"
                          title="Check for new PDFs added to the folder"
                        >
                          {rescanMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <FolderSync className="w-4 h-4 mr-2 group-hover/scan:rotate-180 transition-transform duration-500" />
                          )}
                          Scan for New Files
                        </button>

                        {/* Extract new files only */}
                        <button
                          onClick={handleExtractNew}
                          disabled={isAnyMutationPending || repoStatus?.unprocessed_files === 0}
                          className="btn text-white shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all group/extract"
                          style={{ background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)' }}
                          title="Extract only new (unprocessed) PDFs"
                        >
                          {extractNewMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Plus className="w-4 h-4 mr-2 group-hover/extract:rotate-90 transition-transform" />
                          )}
                          Extract New PDFs ({repoStatus?.unprocessed_files || 0})
                        </button>

                        {/* Reprocess all files */}
                        <button
                          onClick={handleReprocessAll}
                          disabled={isAnyMutationPending}
                          className="btn text-white shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all group/reprocess"
                          style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' }}
                          title="Re-extract ALL PDFs (useful when extraction logic has improved)"
                        >
                          {reprocessAllMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <RotateCcw className="w-4 h-4 mr-2 group-hover/reprocess:rotate-180 transition-transform duration-500" />
                          )}
                          Re-Extract All ({repoStatus?.total_files || 0})
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleCancelExtraction}
                        className="btn text-white px-6 py-3 shadow-lg shadow-red-500/30"
                        style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}
                      >
                        <StopCircle className="w-5 h-5 mr-2" />
                        Stop Extraction
                      </button>
                    )}
                  </div>

                  {/* Extraction Progress */}
                  {extractionProgress && (
                    <div className="relative mt-5 p-5 bg-white/80 backdrop-blur-sm rounded-xl border border-purple-100 shadow-lg">
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-semibold text-gray-900 flex items-center gap-2">
                          {extractionProgress.status === 'running' && (
                            <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                          )}
                          {extractionProgress.status === 'running' ? 'Extracting...' :
                           extractionProgress.status === 'completed' ? 'Extraction Complete!' :
                           extractionProgress.status === 'failed' ? 'Extraction Failed' :
                           extractionProgress.status === 'cancelled' ? 'Extraction Cancelled' :
                           'Starting...'}
                        </span>
                        <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                          {extractionProgress.processed} / {extractionProgress.total} files
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="h-4 bg-gray-100 rounded-full overflow-hidden mb-4">
                        <div
                          className={`h-full transition-all duration-500 rounded-full ${
                            extractionProgress.status === 'completed' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                            extractionProgress.status === 'failed' ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                            extractionProgress.status === 'cancelled' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
                            'bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 animate-shimmer'
                          }`}
                          style={{
                            width: `${extractionProgress.percent}%`,
                            backgroundSize: '200% 100%'
                          }}
                        />
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                          <p className="text-2xl font-bold text-green-600">{extractionProgress.successful}</p>
                          <p className="text-xs text-green-700 font-medium">Successful</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-100">
                          <p className="text-2xl font-bold text-red-600">{extractionProgress.failed}</p>
                          <p className="text-xs text-red-700 font-medium">Failed</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
                          <p className="text-2xl font-bold text-purple-600">{extractionProgress.percent.toFixed(0)}%</p>
                          <p className="text-xs text-purple-700 font-medium">Progress</p>
                        </div>
                      </div>

                      {extractionProgress.message && (
                        <p className="mt-4 text-sm text-gray-600 text-center bg-gray-50 rounded-lg py-2 px-4">
                          {extractionProgress.message}
                        </p>
                      )}

                      {/* Duplicate Files Section */}
                      {extractionProgress.duplicate_files?.length > 0 && (
                        <div className="mt-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
                          <div className="flex items-center mb-3">
                            <Copy className="w-4 h-4 text-gray-500 mr-2" />
                            <span className="font-semibold text-gray-700">
                              Duplicate Files Detected ({extractionProgress.duplicate_files.length})
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">
                            These files have identical content to already processed files and were skipped:
                          </p>
                          <ul className="text-sm text-gray-600 space-y-1.5 max-h-32 overflow-y-auto">
                            {extractionProgress.duplicate_files.map((file, idx) => (
                              <li key={idx} className="flex items-center p-2 bg-white rounded-lg">
                                <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                                {file}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Review Files Section */}
                      {extractionProgress.review_files?.length > 0 && (
                        <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                          <div className="flex items-center mb-3">
                            <AlertTriangle className="w-4 h-4 text-amber-600 mr-2" />
                            <span className="font-semibold text-amber-700">
                              Files Needing Review ({extractionProgress.review_files.length})
                            </span>
                          </div>
                          <p className="text-xs text-amber-600 mb-3">
                            These files have low extraction confidence and may need manual verification:
                          </p>
                          <ul className="text-sm text-amber-700 space-y-1.5 max-h-32 overflow-y-auto">
                            {extractionProgress.review_files.map((file, idx) => (
                              <li key={idx} className="flex items-center p-2 bg-white/80 rounded-lg">
                                <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                                {file}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info text */}
                  {!extractionProgress && (
                    <div className="relative mt-5 space-y-2 text-sm text-gray-600">
                      <p className="flex items-center p-2 bg-white/60 rounded-lg">
                        <Check className="w-4 h-4 text-green-500 mr-2" />
                        {repoStatus?.unprocessed_files || 0} unprocessed files ready for extraction
                      </p>
                      <p className="flex items-center p-2 bg-white/60 rounded-lg">
                        <Check className="w-4 h-4 text-green-500 mr-2" />
                        Uses OCR for scanned documents + AI for intelligent data extraction
                      </p>
                      <p className="flex items-center p-2 bg-white/60 rounded-lg">
                        <Check className="w-4 h-4 text-green-500 mr-2" />
                        Extracts section numbers, dimensions, alloys, and technical specs
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Processing Status */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2 text-primary-600" />
                  Processing Status
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-success-50 to-emerald-50 rounded-xl border border-success-100 hover:shadow-md transition-all">
                    <div className="flex items-center">
                      <div className="p-2 rounded-lg bg-success-100">
                        <Check className="w-5 h-5 text-success-600" />
                      </div>
                      <span className="ml-3 text-success-700 font-medium">Processed</span>
                    </div>
                    <span className="font-bold text-success-700 bg-success-100 px-4 py-1.5 rounded-full">
                      {repoStatus?.processed_files || 0} files
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-warning-50 to-amber-50 rounded-xl border border-warning-100 hover:shadow-md transition-all">
                    <div className="flex items-center">
                      <div className="p-2 rounded-lg bg-warning-100">
                        <Clock className="w-5 h-5 text-warning-600" />
                      </div>
                      <span className="ml-3 text-warning-700 font-medium">Unprocessed</span>
                    </div>
                    <span className="font-bold text-warning-700 bg-warning-100 px-4 py-1.5 rounded-full">
                      {repoStatus?.unprocessed_files || 0} files
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-danger-50 to-rose-50 rounded-xl border border-danger-100 hover:shadow-md transition-all">
                    <div className="flex items-center">
                      <div className="p-2 rounded-lg bg-danger-100">
                        <X className="w-5 h-5 text-danger-600" />
                      </div>
                      <span className="ml-3 text-danger-700 font-medium">Failed</span>
                    </div>
                    <span className="font-bold text-danger-700 bg-danger-100 px-4 py-1.5 rounded-full">
                      {repoStatus?.failed_files || 0} files
                    </span>
                  </div>
                </div>

                {/* Overall Progress */}
                <div className="mt-5 p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
                  <div className="flex justify-between text-sm text-gray-600 mb-3">
                    <span className="font-medium">Overall Progress</span>
                    <span className="font-bold text-primary-600">
                      {((repoStatus?.processed_files / repoStatus?.total_files) * 100 || 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="progress-bar h-3">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${(repoStatus?.processed_files / repoStatus?.total_files) * 100 || 0}%`
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Zap className="w-4 h-4 mr-2 text-accent-cyan" />
                  Recent Activity
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-5 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 hover:shadow-md transition-all">
                    <p className="text-sm text-gray-600 mb-2">Files Added Today</p>
                    <p className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                      {repoStatus?.files_added_today || 0}
                    </p>
                  </div>
                  <div className="p-5 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-100 hover:shadow-md transition-all">
                    <p className="text-sm text-gray-600 mb-2">Files Modified Today</p>
                    <p className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                      {repoStatus?.files_modified_today || 0}
                    </p>
                  </div>
                </div>
              </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* FTP Server Configuration */}
      <FTPConfig />

      {/* Drive Not Mounted Modal */}
      {mountInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMountInstructions(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center">
                <div className="p-3 bg-amber-100 rounded-xl mr-4">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Drive Not Mounted</h3>
                  <p className="text-sm text-gray-600">
                    {mountInstructions.message}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Add this line to <strong>docker-compose.yml</strong> under backend → volumes:</p>
                <div className="bg-gray-900 rounded-lg p-4">
                  <code className="text-green-400 text-sm font-mono break-all">
                    {mountInstructions.volumeLine}
                  </code>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-2">Then restart Docker:</p>
                <div className="bg-gray-900 rounded-lg p-4">
                  <code className="text-yellow-400 text-sm font-mono">
                    docker-compose down && docker-compose up -d
                  </code>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setMountInstructions(null)}
                className="btn btn-primary"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
